import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { Modal } from '@/components/ui/Modal'
import { Toggle } from '@/components/ui/Toggle'
import { ImageUpload } from '@/components/ui/ImageUpload'
import {
  fetchAdminOptions, fetchAdminCategories, fetchAdminModels,
  createOption, updateOption, deleteOption,
} from '@/lib/api'
import type { OptionWithModels } from '@/lib/api'
import type { Category, Model } from '@/types'

const EMPTY = {
  category_id: 0, name: '', description: '', price: 0,
  weight_impact: 0, image_url: null, layer_image_url: null,
  sort_order: 99, is_active: 1, model_ids: [] as number[],
}

function OptionForm({
  init, categories, models, onSave, onClose,
}: {
  init: Partial<OptionWithModels>
  categories: Category[]
  models: Model[]
  onSave: (o: OptionWithModels) => void
  onClose: () => void
}) {
  const [form, setForm] = useState({ ...EMPTY, ...init, model_ids: init.model_ids ?? [] })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isNew = !init.id

  function toggleModel(modelId: number) {
    setForm((f) => ({
      ...f,
      model_ids: f.model_ids.includes(modelId)
        ? f.model_ids.filter((id) => id !== modelId)
        : [...f.model_ids, modelId],
    }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const saved = isNew
        ? await createOption(form)
        : await updateOption(init.id!, form)
      onSave(saved)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          required
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
        <textarea
          rows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-y"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
          <select
            value={form.category_id}
            onChange={(e) => setForm((f) => ({ ...f, category_id: Number(e.target.value) }))}
            required
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            <option value={0}>— select —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Price ($)</label>
          <input
            type="number"
            min={0}
            value={form.price}
            onChange={(e) => setForm((f) => ({ ...f, price: Number(e.target.value) }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Weight Impact (lbs)</label>
          <input
            type="number"
            value={form.weight_impact}
            onChange={(e) => setForm((f) => ({ ...f, weight_impact: Number(e.target.value) }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Sort Order</label>
          <input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>
      <ImageUpload
        label="Option Image"
        value={form.image_url ?? null}
        onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
      />

      {!isNew && (
        <Toggle
          checked={!!form.is_active}
          onChange={(v) => setForm((f) => ({ ...f, is_active: v ? 1 : 0 }))}
          label="Active"
        />
      )}

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-2">Available On Models</label>
        <div className="grid grid-cols-2 gap-2">
          {models.map((m) => (
            <label key={m.id} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.model_ids.includes(m.id)}
                onChange={() => toggleModel(m.id)}
                className="rounded border-slate-300 text-brand-500 focus:ring-brand-400"
              />
              <span className="text-sm text-slate-700">{m.name}</span>
            </label>
          ))}
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : isNew ? 'Create Option' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

export default function AdminOptions() {
  const [options, setOptions] = useState<OptionWithModels[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [models, setModels] = useState<Model[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'add' | OptionWithModels | null>(null)
  const [search, setSearch] = useState('')
  const [filterCat, setFilterCat] = useState<number | null>(null)

  useEffect(() => {
    Promise.all([fetchAdminOptions(), fetchAdminCategories(), fetchAdminModels()])
      .then(([opts, cats, mods]) => { setOptions(opts); setCategories(cats); setModels(mods) })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  function handleSaved(o: OptionWithModels) {
    setOptions((prev) => {
      const idx = prev.findIndex((x) => x.id === o.id)
      return idx >= 0 ? prev.map((x) => x.id === o.id ? o : x) : [...prev, o]
    })
    setModal(null)
  }

  async function handleDelete(o: OptionWithModels) {
    if (!confirm(`Delete "${o.name}"? This cannot be undone.`)) return
    try {
      await deleteOption(o.id)
      setOptions((prev) => prev.filter((x) => x.id !== o.id))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]))

  const visible = options.filter((o) => {
    if (filterCat !== null && o.category_id !== filterCat) return false
    if (search && !o.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  return (
    <AdminLayout>
      <PageHeader
        title="Options"
        subtitle="Manage upgrades, model assignments, and pricing"
        action={
          <button
            onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Option
          </button>
        }
      />

      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Search options…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 w-56"
        />
        <select
          value={filterCat ?? ''}
          onChange={(e) => setFilterCat(e.target.value ? Number(e.target.value) : null)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        >
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Category</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden md:table-cell">Price</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {visible.map((opt) => (
                <tr key={opt.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-5 py-3 font-medium text-slate-800">{opt.name}</td>
                  <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">{catMap[opt.category_id] ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-600 hidden md:table-cell">${opt.price.toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${opt.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {opt.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setModal(opt)} className="text-slate-400 hover:text-brand-600 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(opt)} className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-slate-400 text-sm">No options found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal
          title={modal === 'add' ? 'Add Option' : `Edit — ${(modal as OptionWithModels).name}`}
          onClose={() => setModal(null)}
          size="lg"
        >
          <OptionForm
            init={modal === 'add' ? {} : (modal as OptionWithModels)}
            categories={categories}
            models={models}
            onSave={handleSaved}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
    </AdminLayout>
  )
}
